// /setup — first-run wizard: shown when no users exist yet.
// Creates the workspace and the first workspace_owner account, then logs in.

import { useState, type FormEvent } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useMutation } from '@tanstack/react-query'
import { ApiError, ApiUnreachableError, authApi, rateLimitMessage } from '../lib/api-client'
import { useAuth } from '../lib/auth'
import { Field } from '../components/ui'
import { LogoMark } from '../components/logo'
import { useIllustrations } from '../lib/illustrations'

export function SetupPage() {
  const navigate = useNavigate()
  const { login } = useAuth()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [illustrations] = useIllustrations()

  const setupMutation = useMutation({
    mutationFn: authApi.setup,
    onSuccess: (result) => {
      login(result.token)
      void navigate({ to: '/welcome' })
    },
  })

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    setupMutation.mutate({ email: email.trim(), name: name.trim(), password })
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-canvas">
      {illustrations ? <div aria-hidden className="login-scene" /> : null}

      <div className="relative mx-auto grid min-h-screen w-full items-center justify-items-center px-4 py-12">
        <div className="w-full max-w-[420px]">
          {illustrations ? (
            <h1 className="mb-4">
              <img
                src="/brand/wordmark.webp"
                alt="Goblic"
                width={1000}
                height={454}
                className="mx-auto h-auto w-full max-w-[420px] select-none drop-shadow-[0_14px_18px_rgb(var(--shadow)/0.16)]"
              />
            </h1>
          ) : (
            <div className="mb-6 flex items-center gap-3">
              <LogoMark size={44} />
              <span className="font-display text-xl font-semibold tracking-[-0.02em] text-ink">
                Goblic
              </span>
            </div>
          )}

          <div className="rounded-[20px] border border-line bg-surface/95 p-8 shadow-overlay backdrop-blur-sm">
            <h2 className="font-display text-xl font-semibold tracking-[-0.01em] text-ink">
              Set up your workspace
            </h2>
            <p className="mt-1 text-base text-ink-2">
              Create your admin account to get started.
            </p>

            <form className="mt-7 space-y-4" onSubmit={handleSubmit}>
              <Field label="Your name" htmlFor="setup-name">
                <input
                  id="setup-name"
                  type="text"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  required
                  autoComplete="name"
                  autoFocus
                  className="field-input h-11 rounded-[10px]"
                />
              </Field>
              <Field label="Email" htmlFor="setup-email">
                <input
                  id="setup-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  className="field-input h-11 rounded-[10px]"
                />
              </Field>
              <Field label="Password" htmlFor="setup-password">
                <input
                  id="setup-password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="field-input h-11 rounded-[10px]"
                />
              </Field>

              {setupMutation.isError ? (
                <p
                  className="rounded-[10px] border border-danger/30 bg-danger-soft px-3 py-2 text-base text-ink"
                  role="alert"
                >
                  {setupMutation.error instanceof ApiUnreachableError
                    ? `${setupMutation.error.message} Check that the API is running.`
                    : rateLimitMessage(setupMutation.error)
                      ? rateLimitMessage(setupMutation.error)
                    : setupMutation.error instanceof ApiError && setupMutation.error.status === 409
                      ? 'Setup has already been completed. Go to the login page.'
                      : `Setup failed: ${(setupMutation.error as Error).message}`}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={setupMutation.isPending}
                className="btn-primary h-11 w-full rounded-[10px] text-md"
              >
                {setupMutation.isPending ? 'Creating account…' : 'Create account'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
